import { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";
import { useCompany } from "./CompanyContext";

const NotificationsContext = createContext(null);

const POLL_MS = 30000;

export function NotificationsProvider({ children }) {
  const { user } = useAuth();
  const { activeCompanyId } = useCompany();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unresolvedInquiries, setUnresolvedInquiries] = useState(0);
  const timerRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const reqs = [api.get(`/notifications?limit=20`)];
      if (activeCompanyId) {
        reqs.push(api.get(`/metrics?company_id=${activeCompanyId}`));
      }
      const results = await Promise.all(reqs);
      const notif = results[0]?.data;
      if (notif) {
        setNotifications(notif.items || []);
        setUnreadCount(notif.unread_count || 0);
      }
      const metrics = results[1]?.data;
      if (metrics) {
        // Sidebar badge counts only inquiries the *viewer* should answer.
        // Falls back to total unresolved for back-compat if directed_to_me isn't returned yet.
        const me = metrics?.questions?.directed_to_me;
        setUnresolvedInquiries(typeof me === "number" ? me : (metrics?.questions?.unresolved || 0));
      }
    } catch (e) {
      console.warn("Notifications refresh failed", e);
    }
  }, [user, activeCompanyId]);

  useEffect(() => {
    if (!user) return;
    refresh();
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(refresh, POLL_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [user, refresh]);

  const markRead = useCallback(async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setNotifications((xs) => xs.map((n) => (n.id === id ? { ...n, read: true } : n)));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (e) {
      console.warn("Mark notification read failed", e);
    }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await api.post("/notifications/read-all");
      setNotifications((xs) => xs.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (e) {
      console.warn("Mark all notifications read failed", e);
    }
  }, []);

  const value = useMemo(
    () => ({ notifications, unreadCount, unresolvedInquiries, refresh, markRead, markAllRead }),
    [notifications, unreadCount, unresolvedInquiries, refresh, markRead, markAllRead]
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  return useContext(NotificationsContext);
}
